import { appConfig } from '../config/app.config';
import h, { appData, getReduxGlobal } from './common';
import c, { configTypes, possibleLanguages } from '../config/constants';
import user from '../helpers/user';
import { getConfiguration } from '../hooks/useConfiguration';
import { addScreenHistory } from '../store/reducers/screenHistorySlice';
import { store } from '../store';

interface UrlParams {
    [key: string]: string | number | boolean | null;
}

interface ScreenHistory {
    screenName: string;
    params: string;
}

const url = {
    /**
     * Function to get default page.
     * @returns {string}
     */
    getDefaultPage: (): string => {
        try {
            // ✅ FIXED: Using React Query configuration instead of appData
            const config = getConfiguration();
            return config?.default_screen?.name || c.DEFAULT_SCREEN;
        } catch (e) {
            return c.DEFAULT_SCREEN;
        }
    },
    /**
     * Function to create url with screen name and parameters.
     * @param path
     * @param params
     * @returns {string}
     */
    route: (path: string, params: UrlParams | string = {}): string => {
        const currentLang = h.getLang();
        const lang = currentLang === appConfig.defaultLanguage ? '' : currentLang;
        const defaultScreen = url.getDefaultPage();
        const keys = Object.keys(params);
        let finalURL: string | null = null;
        let query: string;

        if (typeof params === 'string') {
            query = params;
        } else {
            query = keys
                .map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(params[key] as string)}`)
                .join('&');
            query = query !== '' ? '?' + query : '';
        }

        if (path === defaultScreen) {
            path = '';
        }

        finalURL = lang ? `/${lang}/${path}${query}` : `/${path}${query}`;
        return finalURL.replace('&?', '&');
    },
    /**
     * Function to check if the given screen name is the current route.
     * @param path
     * @returns {boolean}
     */
    isCurrentRoute: (path: string): boolean => {
        const currentLang = h.getLang();
        const lang = currentLang === appConfig.defaultLanguage ? '' : currentLang;
        const defaultScreen = url.getDefaultPage();
        if (path === defaultScreen && url.getCurrentPageName() === '') {
            return true;
        }
        return window.location.href.includes(lang ? `/${lang}/${path}` : `/${path}`);
    },
    /**
     * Function to get the current screen name.
     * @returns {string}
     */
    getCurrentPageName: (): string => {
        const currentLang = h.getLang();
        const lang = currentLang === appConfig.defaultLanguage ? '' : currentLang;
        const paths = window.location.href.split('/');
        let route = lang && paths[3] === lang ? paths[4] : paths[3] || '';
        route = route?.includes('?') ? route?.substr(0, route?.search(/\?/)) : route;
        return route?.replace(/\/$/, '');
    },
    /**
     * Function to get language from current URL.
     * @returns {string}
     */
    getLanguage: (): string => {
        const parts = window.location.href.split('/');
        if (parts[4] && parts[4].length === 2 && possibleLanguages.includes(parts[4])) {
            return parts[4];
        }
        return appConfig.defaultLanguage;
    },
    /**
     * Function to access specific URL query.
     * @param key
     * @returns {string | null}
     */
    getQuery: (key: string): string | null => {
        return new URLSearchParams(window.location.search).get(key);
    },
    /**
     * Function to get all query parameters in an array.
     * @returns {Array<{ key: string, value: string }>}
     */
    getAllParams: (): Array<{ key: string; value: string }> => {
        const urlParams = new URLSearchParams(window.location.search);
        const queryParams: Array<{ key: string; value: string }> = [];
        for (const [key, value] of urlParams) {
            queryParams.push({ key, value });
        }
        return queryParams;
    },
    /**
     * Function to get the configured details page type's screen name based on given type.
     * @param type
     * @returns {*}
     */
    getDetailsPage: (type: string): any => {
        // ✅ FIXED: filterObject already uses getConfiguration() internally, no need for Redux state
        return h.filterObject(null, configTypes.DETAIL_SCREENS, type);
    },
    /**
     * Returns external mobile links object
     * @returns {object}
     */
    getMobileAppLinks: (): any => {
        const appStore = h.filterObject(appData, configTypes.EXTERNAL_LINKS, 'mobileAppLinks');
        let mobileAppLinks = appStore.primaryLinks;
        if (
            'secondaryLinks' in appStore &&
            typeof appStore.secondaryLinks !== 'undefined' &&
            appStore.secondaryLinks !== null &&
            appStore.secondaryLinks !== 'null' &&
            'operators' in appStore.secondaryLinks
        ) {
            const userInfo = user.getUserInfo();
            if (appStore.secondaryLinks.operators.indexOf(userInfo.operator) !== -1) {
                mobileAppLinks = appStore.secondaryLinks;
            }
        }
        return mobileAppLinks;
    },
    navigate: null as ((url: string, options: { replace: boolean }) => void) | null,
    /**
     * Function to insert parameters into the URL.
     * @param key
     * @param value
     */
    insertParams(key: string | UrlParams, value: string | null = null): void {
        if (!url.navigate) {
            return;
        } else if (typeof key === 'string' && !value) {
            return;
        }
        const currentUrl = new URL(window.location.href);
        if (typeof key === 'object') {
            for (const [paramKey, paramValue] of Object.entries(key)) {
                currentUrl.searchParams.set(paramKey, paramValue as string);
            }
        } else {
            currentUrl.searchParams.set(key, value as string);
        }
        url.navigate(currentUrl.pathname + currentUrl.search + currentUrl.hash, { replace: true });
    },

    /**
     * Function to insert parameters into the URL.
     * @param key
     * @param value
     */
    insertParamsWithoutReload(searchParams: { [key: string]: string }): void {
        const keysArr = Object.keys(searchParams || {});
        if (keysArr.length === 0) {
            return;
        }
        const params = new URLSearchParams(location.search);

        Object.keys(searchParams).map((key) => {
            if (typeof key === 'string' && searchParams[key]) {
                params.set(key, searchParams[key]);
            }
        });
        window.history.replaceState(null, '', `${window.location.pathname}?${params.toString()}`);
    },
    /**
     * Function to decode a base64 encoded value.
     * @param val
     * @returns {string | null}
     */
    decodeValue(val: string): string | null {
        try {
            return window.atob(val);
        } catch (e) {
            return null;
        }
    },
    /**
     * Function to split a URL into its components.
     * @param inputUrl
     * @returns {object}
     */
    splitURL(inputUrl: string): {
        url: string;
        protocol: string | null;
        hostName: string | null;
        domain: string | null;
        subDomain: string | null;
        tld: string | null;
    } {
        const parts = {
            url: inputUrl,
            protocol: null,
            hostName: null,
            domain: null,
            subDomain: null,
            tld: null,
        };
        try {
            const urlObj = new URL(inputUrl);
            const domainParts = urlObj.hostname.split('.') || [];
            if (domainParts.length >= 2) {
                parts.hostName = urlObj.hostname;
                parts.protocol = urlObj.protocol;
                parts.domain = domainParts.slice(-2).join('.');
                parts.tld = domainParts.slice(-1)[0];
                parts.subDomain = domainParts.length > 2 ? domainParts[0] : null;
            }
        } catch (e) {
            return parts;
        }
        return parts;
    },
    /**
     * Function to get the subdomain from a URL.
     * @param inputUrl
     * @returns {string}
     */
    getSubDomain(inputUrl: string): string {
        if (inputUrl.indexOf('http') === -1) {
            return '';
        }
        const urlParts = url.splitURL(inputUrl);
        return urlParts?.subDomain || '';
    },
};

export const replaceHttpWithHttps = (inputUrl: string): string => {
    if (typeof inputUrl !== 'string') {
        return '';
    }
    return inputUrl.replace(/http:/g, 'https:');
};

export const addToScreenHistory = (screenName: string): void => {
    let params = window.location.search ? window.location.search : '';
    params += window.location.hash ? window.location.hash : '';
    store.dispatch(addScreenHistory({ screenName, params }));
};

export const getPreviousScreen = (): ScreenHistory | null => {
    const state = store.getState();
    const screenHistory = state.screenHistory && Array.isArray(state.screenHistory) ? state.screenHistory : [];
    return screenHistory[screenHistory.length - 2] ? screenHistory[screenHistory.length - 2] : null;
};

export const getPreviousScreenRoute = (fallbackScreen = '', fallbackParams = ''): string => {
    const previousScreen = getPreviousScreen();
    return previousScreen
        ? url.route(previousScreen.screenName, previousScreen.params)
        : url.route(fallbackScreen, fallbackParams);
};

export const addParamsToQueryString = (inputUrl: string, params: UrlParams): string => {
    Object.keys(params).forEach((k) => {
        if (!params[k]) {
            return;
        }
        inputUrl = `${inputUrl}${inputUrl.includes('?') ? '&' : '?'}${k}=${encodeURIComponent(params[k] as string)}`;
    });
    return inputUrl;
};

export default url;
